import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  Check,
  ExternalLink,
  Loader2,
  Minus,
  Plus,
  Search,
  ShoppingCart,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ahService, type AHProduct } from '@/services/ah-service'
import { isPantryStaple, rankAHProducts } from '@/services/ah-ranking'
import { ahMappingApi, normalizeIngredientKey } from '@/services/ah-mapping-api'
import { aggregateBasket } from '@/services/menu-service'
import type { BasketItem } from '@/types/menu'

interface AHOrderDialogProps {
  open: boolean
  onClose: () => void
  items: BasketItem[]
}

interface OrderRow {
  key: string
  ingredientName: string
  candidates: AHProduct[]
  selectedId: number | null
  quantity: number
  included: boolean
  expanded: boolean
  searchTerm: string
  isSearching: boolean
}

const formatPrice = (price: number | null) =>
  typeof price === 'number' ? `€${price.toFixed(2).replace('.', ',')}` : ''

export function AHOrderDialog({ open, onClose, items }: AHOrderDialogProps) {
  const [rows, setRows] = useState<OrderRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ingredientNames = useMemo(
    () => aggregateBasket(items).all_items.map((item) => item.name).filter(Boolean),
    [items]
  )

  const buildRows = useCallback(async () => {
    if (!ingredientNames.length) {
      setRows([])
      return
    }

    setIsLoading(true)
    setError(null)

    const [mappings, search] = await Promise.all([
      ahMappingApi.getMappings(ingredientNames),
      ahService.search(ingredientNames),
    ])

    if (!search.success) {
      setError(search.error || 'Albert Heijn is nu niet bereikbaar. Probeer het later opnieuw.')
      setRows([])
      setIsLoading(false)
      return
    }

    const results = search.results ?? {}

    const nextRows: OrderRow[] = ingredientNames.map((name) => {
      const key = normalizeIngredientKey(name)
      // AH's own ordering puts crisps above onions; rank by how well each
      // product actually matches the ingredient.
      const candidates = rankAHProducts(name, results[name] ?? [])
      const mapping = mappings[key]

      if (mapping && !candidates.some((product) => product.webshopId === mapping.webshopId)) {
        candidates.unshift({
          webshopId: mapping.webshopId,
          title: mapping.productTitle,
          price: null,
          unitSize: mapping.unitSize ?? '',
          imageUrl: '',
          available: true,
        })
      }

      const selectedId = mapping?.webshopId ?? candidates[0]?.webshopId ?? null

      return {
        key,
        ingredientName: name,
        candidates,
        selectedId,
        quantity: 1,
        included: selectedId != null && !isPantryStaple(name),
        expanded: false,
        searchTerm: name,
        isSearching: false,
      }
    })

    setRows(nextRows)
    setIsLoading(false)
  }, [ingredientNames])

  useEffect(() => {
    if (open) {
      void buildRows()
    } else {
      setRows([])
      setError(null)
    }
  }, [open, buildRows])

  const updateRow = (key: string, updater: (row: OrderRow) => OrderRow) =>
    setRows((current) => current.map((row) => (row.key === key ? updater(row) : row)))

  const handleRowSearch = async (row: OrderRow) => {
    const term = row.searchTerm.trim()
    if (!term) {
      return
    }

    updateRow(row.key, (current) => ({ ...current, isSearching: true }))
    const result = await ahService.search([term])
    updateRow(row.key, (current) => {
      const found = rankAHProducts(term, result.results?.[term] ?? [])
      return {
        ...current,
        isSearching: false,
        candidates: found.length ? found : current.candidates,
        selectedId: found.length ? found[0].webshopId : current.selectedId,
        included: found.length ? true : current.included,
        expanded: found.length > 1,
      }
    })
  }

  const selectedRows = rows.filter(
    (row) => row.included && row.selectedId != null && row.quantity > 0
  )
  const matchedCount = rows.filter((row) => row.selectedId != null).length

  const handleOrder = async () => {
    const orderItems = selectedRows
      .map((row) => ({ webshopId: row.selectedId as number, quantity: row.quantity, row }))
      .filter((entry) => entry.webshopId)

    await Promise.all(
      orderItems.map((entry) => {
        const product = entry.row.candidates.find((candidate) => candidate.webshopId === entry.webshopId)
        return product ? ahMappingApi.saveMapping(entry.row.ingredientName, product) : Promise.resolve()
      })
    )

    const url = ahService.buildAddToCartUrl(
      orderItems.map((entry) => ({ webshopId: entry.webshopId, quantity: entry.quantity }))
    )
    window.open(url, '_blank', 'noopener,noreferrer')
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Bestellen bij Albert Heijn</DialogTitle>
          <DialogDescription>
            Controleer welk AH-product bij elk ingrediënt hoort. We openen daarna je AH-winkelmandje
            met deze producten.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-slate-300">
            <Loader2 className="mb-3 h-7 w-7 animate-spin text-orange-600" />
            <p>Producten zoeken bij Albert Heijn...</p>
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-600 dark:text-slate-300">
            Geen ingrediënten om te bestellen.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-slate-400">
              {matchedCount} van {rows.length} ingrediënten gekoppeld aan een AH-product.
            </p>

            <ul className="space-y-2">
              {rows.map((row) => {
                const selected = row.candidates.find((product) => product.webshopId === row.selectedId)
                return (
                  <li
                    key={row.key}
                    className={`rounded-lg border p-3 ${
                      row.included && selected
                        ? 'border-gray-200 dark:border-slate-700'
                        : 'border-dashed border-gray-300 dark:border-slate-700'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={row.included && Boolean(selected)}
                        disabled={!selected}
                        onChange={(event) =>
                          updateRow(row.key, (current) => ({ ...current, included: event.target.checked }))
                        }
                        className="mt-1 h-4 w-4 flex-shrink-0 accent-orange-600"
                        aria-label={`${row.ingredientName} meenemen`}
                      />

                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500">
                          {row.ingredientName}
                        </p>
                        {selected ? (
                          <p className="text-sm font-medium text-gray-900 dark:text-slate-50">
                            {selected.title}
                            {selected.unitSize ? (
                              <span className="ml-1 font-normal text-gray-500 dark:text-slate-400">
                                · {selected.unitSize}
                              </span>
                            ) : null}
                            {selected.price != null ? (
                              <span className="ml-1 font-normal text-gray-500 dark:text-slate-400">
                                · {formatPrice(selected.price)}
                              </span>
                            ) : null}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            Geen AH-product gevonden — voeg dit los toe in Albert Heijn.
                          </p>
                        )}

                        <button
                          type="button"
                          className="mt-1 text-xs font-medium text-orange-600 hover:underline"
                          onClick={() =>
                            updateRow(row.key, (current) => ({ ...current, expanded: !current.expanded }))
                          }
                        >
                          {row.expanded ? 'Verberg opties' : 'Ander product kiezen'}
                        </button>

                        {row.expanded && (
                          <div className="mt-2 flex gap-2">
                            <Input
                              value={row.searchTerm}
                              onChange={(event) =>
                                updateRow(row.key, (current) => ({
                                  ...current,
                                  searchTerm: event.target.value,
                                }))
                              }
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault()
                                  void handleRowSearch(row)
                                }
                              }}
                              placeholder="Zoek een ander product bij AH"
                              className="h-8 text-xs"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 flex-shrink-0"
                              onClick={() => void handleRowSearch(row)}
                              disabled={row.isSearching}
                            >
                              {row.isSearching ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Search className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        )}

                        {row.expanded && (
                          <ul className="mt-2 space-y-1">
                            {row.candidates.map((product) => (
                              <li key={product.webshopId}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateRow(row.key, (current) => ({
                                      ...current,
                                      selectedId: product.webshopId,
                                      included: true,
                                      expanded: false,
                                    }))
                                  }
                                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs ${
                                    product.webshopId === row.selectedId
                                      ? 'bg-orange-50 text-orange-800 dark:bg-orange-950 dark:text-orange-100'
                                      : 'hover:bg-gray-50 dark:hover:bg-slate-800'
                                  }`}
                                >
                                  {product.webshopId === row.selectedId ? (
                                    <Check className="h-3 w-3 flex-shrink-0" />
                                  ) : (
                                    <span className="h-3 w-3 flex-shrink-0" />
                                  )}
                                  <span className="flex-1 truncate">
                                    {product.title}
                                    {product.unitSize ? ` · ${product.unitSize}` : ''}
                                  </span>
                                  <span className="text-gray-500 dark:text-slate-400">
                                    {formatPrice(product.price)}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {selected && (
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() =>
                              updateRow(row.key, (current) => ({
                                ...current,
                                quantity: Math.max(1, current.quantity - 1),
                              }))
                            }
                            disabled={row.quantity <= 1}
                            aria-label="Minder"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-5 text-center text-sm text-gray-700 dark:text-slate-200">
                            {row.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() =>
                              updateRow(row.key, (current) => ({
                                ...current,
                                quantity: Math.min(current.quantity + 1, 20),
                              }))
                            }
                            aria-label="Meer"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>

            <div className="space-y-2 border-t border-gray-200 pt-4 dark:border-slate-700">
              <Button
                variant="primary"
                className="w-full"
                onClick={handleOrder}
                disabled={selectedRows.length === 0}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Open mijn AH-winkelmandje ({selectedRows.length})
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Log in bij Albert Heijn in hetzelfde tabblad. Open deze knop één keer — bij nogmaals
                klikken worden de producten dubbel toegevoegd.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
